import { useContext, useState } from 'react';

import { faCheck, faCopy } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import classNames from 'classnames';

import { useDevtoolsShortcut } from '../../../../../utils/hooks/useDevtoolsShortcut';

import { PreviewSidebarPatientEducation } from './PreviewSidebarPatientEducation';
import { ThemeModeContext } from './QRPrintDownloadContent';

interface PreviewSidebarQRProps {
  qrCodeData: { link: string; qrCode: string; accessCode: number | null };
}

export const PreviewSidebarQR = ({ qrCodeData }: PreviewSidebarQRProps) => {
  const [isCopied, setCopied] = useState(false);
  const { showDevtools } = useDevtoolsShortcut();
  const mode = useContext(ThemeModeContext);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(qrCodeData.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  return (
    <div className="vai-flex vai-flex-col vai-gap-4 vai-items-center vai-h-full vai-w-full vai-p-4">
      <div>
        <div
          className={classNames(
            'vai-my-2 vai-py-2 vai-px-4 vai-flex vai-items-center vai-justify-left vai-rounded-sm vai-w-full',
            {
              'vai-bg-zinc-800': mode === 'dark',
              'vai-bg-zinc-100': mode === 'light',
            },
          )}
        >
          <span
            className={classNames(
              'vai-text-sm vai-font-medium vai-block vai-text-center vai-items-center vai-font-inter',
              {
                'vai-text-foreground-dark': mode === 'dark',
                'vai-text-zinc-900': mode === 'light',
              },
            )}
          >
            <Trans>Patient Education</Trans>
          </span>
        </div>

        <div className="vai-flex vai-flex-col vai-items-start vai-h-fit vai-w-full vai-mt-2 vai-px-2 vai-py-2">
          <span
            className={classNames(
              'vai-text-[12px] vai-font-medium vai-font-sans',
              {
                'vai-text-zinc-200': mode === 'dark',
                'vai-text-zinc-500': mode === 'light',
              },
            )}
          >
            <Trans>VIEW ON MOBILE</Trans>
          </span>
          <span
            className={classNames(
              'vai-text-sm vai-font-extralight vai-font-inter vai-text-white',
              {
                'vai-text-zinc-900': mode === 'light',
                'vai-text-white': mode === 'dark',
              },
            )}
          >
            <Trans>
              Patient can use their mobile camera to access the treatment plan
              on their device.
            </Trans>
          </span>
        </div>

        <div className="vai-bg-white vai-p-4 vai-rounded-lg vai-shadow-lg vai-mt-8 vai-w-full">
          <div className="vai-flex vai-justify-center vai-items-center">
            <img
              src={`data:image/svg+xml;base64,${btoa(qrCodeData.qrCode)}`}
              alt="QR Code"
              className="vai-w-64 vai-h-64"
            />
          </div>

          <div className="vai-flex vai-justify-center vai-mt-4 vai-flex-col vai-gap-2 vai-items-center">
            {qrCodeData.accessCode && (
              <span className="vai-text-md vai-font-medium vai-text-zinc-800">
                <Trans>Access Code:</Trans> {qrCodeData.accessCode}
              </span>
            )}
            {showDevtools && (
              <button
                data-testid="copy-link-button"
                onClick={handleCopyLink}
                className="vai-flex vai-items-center vai-gap-2 vai-px-4 vai-py-2 vai-bg-blue-600 hover:vai-bg-blue-700 vai-text-white vai-rounded-md vai-transition-colors vai-duration-200 vai-text-sm vai-font-medium"
              >
                {isCopied ? (
                  <>
                    <FontAwesomeIcon
                      icon={faCheck}
                      className="vai-w-4 vai-h-4"
                    />
                    <Trans>Copied!</Trans>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon
                      icon={faCopy}
                      className="vai-w-4 vai-h-4"
                    />
                    <Trans>Copy Link</Trans>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="vai-w-full">
        <PreviewSidebarPatientEducation />
      </div>
    </div>
  );
};
